import { all, takeLatest, call, put, select } from 'redux-saga/effects';
import apiPortalService from '@/services/api-portal';
import { ApiDoc } from '@/types';
import { ActionType } from 'deox';
import { navigate } from '@reach/router';
import {
  searchApiDocsActions,
  fetchApiDocsActions,
  voteApiDocActions,
  rateApiDocActions,
  createApiDocActions,
  editApiDocActions,
  deleteApiDocActions,
  fetchApiDocActions,
  downloadApiSwaggerActions,
  fetchApiGroupsActions,
} from '../actions/api-docs';
import { RootState } from '../reducers';
import { fetchSiteMetadataActions } from '../actions';

export function* searchApiDocsSaga(action: ActionType<typeof searchApiDocsActions.request>) {
  try {
    const { apiName } = action.payload;
    const docs: { data: any; paging: any } = yield call(apiPortalService.searchApiDocs, apiName);
    const { groups, apiDocs } = normalizeApiDocs(docs.data);
    const { totalRecords } = docs.paging;
    yield put(searchApiDocsActions.success(groups, apiDocs, totalRecords));
  } catch (err) {
    yield put(searchApiDocsActions.failure(err));
  }
}

export function* fetchApiDocsSaga(action: ActionType<typeof fetchApiDocsActions.request>) {
  try {
    const { organisationId, pageNumber, pageSize, searchText, groupName } = action.payload;
    const {data, paging} = yield call(apiPortalService.fetchApiDocs, organisationId, pageNumber, pageSize, searchText, groupName);
    yield put(fetchApiDocsActions.success(data, paging));
  } catch (error) {
    yield put(fetchApiDocsActions.failure(error));
  }
}

export function* fetchApiGroupsSaga(action: ActionType<typeof fetchApiGroupsActions.request>) {
  try {
    const { organisationId } = action.payload;
    const {data} = yield call(apiPortalService.fetchApiGroups, organisationId);
    yield put(fetchApiGroupsActions.success(data));
  } catch (error) {
    yield put(fetchApiGroupsActions.failure(error));
  }
}

export function* fetchApiDocSaga(action: ActionType<typeof fetchApiDocActions.request>) {
  try {
    const { id, organisationId } = action.payload;
    const api: ApiDoc = yield call(apiPortalService.fetchApiDoc, id, organisationId);

    yield put(fetchApiDocActions.success(api));
  } catch (error) {
    yield put(fetchApiDocActions.failure(error));
  }
}

export function* downloadApiSwaggerSaga(
  action: ActionType<typeof downloadApiSwaggerActions.request>,
) {
  try {
    const { apiId, organisationId } = action.payload;
    const api: ApiDoc = yield call(apiPortalService.fetchApiDoc, apiId, organisationId);

    window.open(api.swagger, '_blank');

    yield put(fetchApiDocActions.success(api));
  } catch (error) {
    yield put(fetchApiDocActions.failure(error));
  }
}

export function* voteApiDocSaga(action: ActionType<typeof voteApiDocActions.request>) {
  try {
    const { id } = action.payload;
    yield call(apiPortalService.voteApiDoc, id);
    yield put(voteApiDocActions.success(id));
  } catch (err) {
    yield put(voteApiDocActions.failure(err));
  }
}

export function* rateApiDocSaga(action: ActionType<typeof rateApiDocActions.request>) {
  try {
    const { id, rating } = action.payload;
    yield call(apiPortalService.rateApiDoc, id, rating);
    yield put(rateApiDocActions.success());
  } catch (err) {
    yield put(rateApiDocActions.failure(err));
  }
}

export function* createApiDocSaga(action: ActionType<typeof createApiDocActions.request>) {
  try {
    const { apiDoc } = action.payload;
    const { id: organisationId, subdomain } = yield select(
      (state: RootState) => state.site.organisation,
    );

    yield call(apiPortalService.createApiDoc, apiDoc, organisationId);
    yield put(createApiDocActions.success());

    // refetch api docs
    yield put(fetchApiDocsActions.request(organisationId));

    // refetch site metadata
    yield put(fetchSiteMetadataActions.request(subdomain));

    navigate(`/dashboard/my-apis`);
  } catch (err) {
    yield put(createApiDocActions.failure(err));
  }
}

export function* editApiDocSaga(action: ActionType<typeof editApiDocActions.request>) {
  try {
    const { id, apiDoc } = action.payload;
    const { id: organisationId, subdomain } = yield select(
      (state: RootState) => state.site.organisation,
    );

    yield call(apiPortalService.updateApiDoc, id, apiDoc, organisationId);
    yield put(editApiDocActions.success());

    // refetch api docs
    yield put(fetchApiDocsActions.request(organisationId));

    // refetch site metadata
    yield put(fetchSiteMetadataActions.request(subdomain));

    navigate(`/dashboard/my-apis`);
  } catch (err) {
    yield put(editApiDocActions.failure(err));
  }
}

export function* deleteApiDocSaga(action: ActionType<typeof deleteApiDocActions.request>) {
  try {
    const { id } = action.payload;
    const { id: organisationId, subdomain } = yield select(
      (state: RootState) => state.site.organisation,
    );
    yield call(apiPortalService.deleteApiDoc, id, organisationId);
    yield put(deleteApiDocActions.success());

    // refetch api docs
    yield put(fetchApiDocsActions.request(organisationId));

    // refetch site metadata
    yield put(fetchSiteMetadataActions.request(subdomain));

    navigate(`/dashboard/my-apis`);
  } catch (err) {
    yield put(deleteApiDocActions.failure(err));
  }
}

export default function* root() {
  yield all([
    takeLatest(searchApiDocsActions.request.type, searchApiDocsSaga),
    takeLatest(fetchApiDocActions.request.type, fetchApiDocSaga),
    takeLatest(fetchApiGroupsActions.request.type, fetchApiGroupsSaga),
    takeLatest(fetchApiDocsActions.request.type, fetchApiDocsSaga),
    takeLatest(downloadApiSwaggerActions.request.type, downloadApiSwaggerSaga),
    takeLatest(voteApiDocActions.request.type, voteApiDocSaga),
    takeLatest(rateApiDocActions.request.type, rateApiDocSaga),
    takeLatest(createApiDocActions.request.type, createApiDocSaga),
    takeLatest(editApiDocActions.request.type, editApiDocSaga),
    takeLatest(deleteApiDocActions.request.type, deleteApiDocSaga),
  ]);
}

const normalizeApiDocs = (docs: ApiDoc[]) => {
  const groups = new Set<string>();

  docs.forEach((doc) => {
    groups.add(doc.groupName);
  });

  return {
    groups: Array.from(groups),
    apiDocs: docs,
  };
};
